<template>
  <div>

    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Document Management</h5>
        <p class="page-sub">Manage and publish company documents for employees</p>
      </div>
      <div class="header-actions">
        <button class="btn-outline" @click="openCategoryModal(null)"><font-awesome-icon icon="tags" /> Categories</button>
        <button v-if="permission.upload_documents" class="btn-primary-action" @click="openCreateDoc">
          <font-awesome-icon icon="plus" /> Upload Document
        </button>
      </div>
    </div>

    <!-- Permission denied -->
    <div v-if="!permission.view_documents" class="alert-box alert-box--warn">
      <font-awesome-icon icon="lock" /> You don't have permission to view documents.
    </div>

    <template v-else>

      <!-- Filter bar -->
      <div class="filter-bar">
        <input v-model="filters.search" class="filter-input" placeholder="Search documents…" @input="debounceSearch" />
        <select v-model="filters.status" class="filter-select" @change="fetchDocuments">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <select v-model="filters.category_id" class="filter-select" @change="fetchDocuments">
          <option value="">All Categories</option>
          <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
        <select v-model="filters.audience" class="filter-select" @change="fetchDocuments">
          <option value="">All Audiences</option>
          <option value="all">All Employees</option>
          <option value="department">Department</option>
          <option value="employee">Specific Employees</option>
        </select>
      </div>

      <!-- Skeleton -->
      <div v-if="loading" class="sk-table">
        <div class="sk-row-head"></div>
        <div v-for="n in 6" :key="n" class="sk-row"></div>
      </div>

      <!-- Empty -->
      <div v-else-if="!documents.length" class="empty-box">
        <div class="empty-illustration"><font-awesome-icon icon="file-alt" class="empty-icon" /></div>
        <p class="empty-title">No documents yet</p>
        <p class="empty-sub">Upload your first document to get started.</p>
      </div>

      <!-- Table -->
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Version</th>
              <th>Audience</th>
              <th>Status</th>
              <th>Reads</th>
              <th>Acks</th>
              <th>Published</th>
              <th>Expires</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in documents" :key="d.id">
              <td class="fw-600 doc-title-cell" @click="openDetail(d)">
                <font-awesome-icon :icon="fileIcon(d.file_type)" class="file-icon" />
                {{ d.title }}
                <span v-if="d.requires_acknowledgement" class="ack-badge" title="Requires acknowledgement">ACK</span>
              </td>
              <td>{{ d.category?.name || '—' }}</td>
              <td><span class="ver-chip">v{{ d.version }}</span></td>
              <td>{{ audienceLabel(d) }}</td>
              <td><span class="chip" :class="statusClass(d.status)">{{ d.status }}</span></td>
              <td class="count-cell">{{ d.reads_count || 0 }}</td>
              <td class="count-cell">{{ d.acknowledgements_count || 0 }}</td>
              <td>{{ fmtDate(d.published_at) }}</td>
              <td :class="{ 'text-danger': isExpired(d.expires_at) }">{{ fmtDate(d.expires_at) }}</td>
              <td class="action-cell">
                <button v-if="d.status === 'draft' && permission.publish_documents" class="icon-btn icon-btn--green" title="Publish" @click="confirmPublish(d)"><font-awesome-icon icon="check-circle" /></button>
                <button v-if="d.status === 'published' && permission.archive_documents" class="icon-btn icon-btn--gray" title="Archive" @click="confirmArchive(d)"><font-awesome-icon icon="archive" /></button>
                <button v-if="d.status !== 'archived' && permission.edit_documents" class="icon-btn icon-btn--blue" title="Edit" @click="openEdit(d)"><font-awesome-icon icon="edit" /></button>
                <button v-if="d.file_path || d.file_name" class="icon-btn icon-btn--teal" title="Download" @click="downloadDoc(d)"><font-awesome-icon icon="download" /></button>
                <button class="icon-btn icon-btn--purple" title="History" @click="openAuditLog(d)"><font-awesome-icon icon="history" /></button>
                <button v-if="permission.delete_documents" class="icon-btn icon-btn--red" title="Delete" @click="confirmDelete(d)"><font-awesome-icon icon="trash" /></button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination" v-if="meta.last_page > 1">
          <button class="page-btn" :disabled="meta.current_page === 1" @click="goPage(meta.current_page - 1)">‹</button>
          <span class="page-info">{{ meta.current_page }} / {{ meta.last_page }}</span>
          <button class="page-btn" :disabled="meta.current_page === meta.last_page" @click="goPage(meta.current_page + 1)">›</button>
        </div>
      </div>
    </template>

    <!-- ══  Create / Edit Document Modal  ══ -->
    <div class="modal-overlay" v-if="docModal.open" @click.self="docModal.open = false">
      <div class="modal-box modal-box--lg">
        <div class="modal-head">
          <p class="modal-title">{{ docModal.isEdit ? 'Edit Document' : 'Upload Document' }}</p>
          <button class="modal-close" @click="docModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-grid">
            <div class="form-row form-row--full">
              <label class="form-label">Title <span class="req">*</span></label>
              <input class="form-input" v-model="docModal.title" placeholder="Document title" />
            </div>
            <div class="form-row form-row--full">
              <label class="form-label">Description</label>
              <textarea class="form-input" rows="3" v-model="docModal.description" placeholder="Optional description…"></textarea>
            </div>
            <div class="form-row">
              <label class="form-label">Category</label>
              <select class="form-input" v-model="docModal.category_id">
                <option value="">— None —</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="form-row">
              <label class="form-label">Version</label>
              <input class="form-input" v-model="docModal.version" placeholder="e.g. 1.0" />
            </div>
            <div class="form-row">
              <label class="form-label">Audience</label>
              <select class="form-input" v-model="docModal.audience" @change="onAudienceChange">
                <option value="all">All Employees</option>
                <option value="department">Department(s)</option>
                <option value="employee">Specific Employee(s)</option>
              </select>
            </div>

            <!-- Department multiselect -->
            <div class="form-row form-row--full" v-if="docModal.audience === 'department'">
              <label class="form-label">Departments <span class="req">*</span></label>
              <div class="ms-wrap">
                <div class="ms-trigger" @click="deptOpen = !deptOpen">
                  <div class="ms-tags" v-if="docModal.audience_ids.length">
                    <span v-for="id in docModal.audience_ids" :key="id" class="ms-tag">
                      {{ deptName(id) }}<button type="button" class="ms-tag-rm" @click.stop="removeAudienceId(id)">×</button>
                    </span>
                  </div>
                  <span v-else class="ms-placeholder">Select departments…</span>
                  <span class="ms-arrow" :class="{ 'ms-arrow--open': deptOpen }">▾</span>
                </div>
                <div v-if="deptOpen" class="ms-dropdown">
                  <input class="ms-search" v-model="deptSearch" placeholder="Search departments…" @click.stop />
                  <div class="ms-list">
                    <label v-for="d in filteredDepts" :key="d.id" class="ms-option" :class="{ 'ms-option--on': docModal.audience_ids.includes(d.id) }">
                      <input type="checkbox" :value="d.id" v-model="docModal.audience_ids" class="ms-check" @click.stop />
                      <span>{{ d.name }}</span>
                    </label>
                    <div v-if="!filteredDepts.length" class="ms-empty">No departments found</div>
                  </div>
                </div>
              </div>
              <div v-if="deptOpen" class="ms-backdrop" @click="deptOpen = false"></div>
            </div>

            <!-- Employee multiselect -->
            <div class="form-row form-row--full" v-if="docModal.audience === 'employee'">
              <label class="form-label">Employees <span class="req">*</span></label>
              <div class="ms-wrap">
                <div class="ms-trigger" @click="empOpen = !empOpen">
                  <div class="ms-tags" v-if="docModal.audience_ids.length">
                    <span v-for="id in docModal.audience_ids" :key="id" class="ms-tag">
                      {{ empName(id) }}<button type="button" class="ms-tag-rm" @click.stop="removeAudienceId(id)">×</button>
                    </span>
                  </div>
                  <span v-else class="ms-placeholder">Select employees…</span>
                  <span class="ms-arrow" :class="{ 'ms-arrow--open': empOpen }">▾</span>
                </div>
                <div v-if="empOpen" class="ms-dropdown">
                  <input class="ms-search" v-model="empSearch" placeholder="Search employees…" @click.stop />
                  <div class="ms-list">
                    <label v-for="e in filteredEmps" :key="e.id" class="ms-option" :class="{ 'ms-option--on': docModal.audience_ids.includes(e.id) }">
                      <input type="checkbox" :value="e.id" v-model="docModal.audience_ids" class="ms-check" @click.stop />
                      <span>{{ e.first_name }} {{ e.last_name }}</span>
                    </label>
                    <div v-if="!filteredEmps.length" class="ms-empty">No employees found</div>
                  </div>
                </div>
              </div>
              <div v-if="empOpen" class="ms-backdrop" @click="empOpen = false"></div>
            </div>
            <div class="form-row">
              <label class="form-label">Expiry Date</label>
              <input class="form-input" type="date" v-model="docModal.expires_at" />
            </div>
            <div class="form-row">
              <label class="form-label">Review Date</label>
              <input class="form-input" type="date" v-model="docModal.review_date" />
            </div>
            <div class="form-row form-row--full">
              <label class="checkbox-wrap">
                <input type="checkbox" v-model="docModal.requires_acknowledgement" />
                <span>Require employee acknowledgement</span>
              </label>
            </div>
            <div class="form-row form-row--full">
              <label class="form-label">{{ docModal.isEdit ? 'Replace File (optional)' : 'File' }}</label>
              <input type="file" class="file-input" @change="onFileSelected" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg" />
              <p v-if="docModal.isEdit && docModal.currentFileName" class="file-hint">Current: {{ docModal.currentFileName }}</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="docModal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving" @click="saveDocument">
            {{ saving ? 'Saving…' : (docModal.isEdit ? 'Save Changes' : 'Upload Document') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══  Document Detail Modal  ══ -->
    <div class="modal-overlay" v-if="detailModal.open" @click.self="detailModal.open = false">
      <div class="modal-box modal-box--lg">
        <div class="modal-head">
          <p class="modal-title">{{ detailModal.doc?.title }}</p>
          <button class="modal-close" @click="detailModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" v-if="detailModal.doc">
          <div class="detail-grid">
            <div class="detail-item"><span class="detail-label">Category</span><span class="detail-value">{{ detailModal.doc.category?.name || '—' }}</span></div>
            <div class="detail-item"><span class="detail-label">Version</span><span class="detail-value">v{{ detailModal.doc.version }}</span></div>
            <div class="detail-item"><span class="detail-label">Status</span><span class="chip" :class="statusClass(detailModal.doc.status)">{{ detailModal.doc.status }}</span></div>
            <div class="detail-item"><span class="detail-label">Audience</span><span class="detail-value">{{ audienceLabel(detailModal.doc) }}</span></div>
            <div class="detail-item"><span class="detail-label">Published</span><span class="detail-value">{{ fmtDate(detailModal.doc.published_at) }}</span></div>
            <div class="detail-item"><span class="detail-label">Expiry</span><span class="detail-value">{{ fmtDate(detailModal.doc.expires_at) }}</span></div>
            <div class="detail-item"><span class="detail-label">Review Date</span><span class="detail-value">{{ fmtDate(detailModal.doc.review_date) }}</span></div>
            <div class="detail-item"><span class="detail-label">Requires Ack.</span><span class="detail-value">{{ detailModal.doc.requires_acknowledgement ? 'Yes' : 'No' }}</span></div>
            <div class="detail-item"><span class="detail-label">Total Reads</span><span class="detail-value fw-600">{{ detailModal.doc.reads_count }}</span></div>
            <div class="detail-item"><span class="detail-label">Acknowledgements</span><span class="detail-value fw-600">{{ detailModal.doc.acknowledgements_count }}</span></div>
          </div>
          <div v-if="detailModal.doc.description" class="notes-box">
            <p class="notes-label">Description</p>
            <p class="notes-content">{{ detailModal.doc.description }}</p>
          </div>

          <!-- Audit log -->
          <div v-if="detailModal.auditLog.length">
            <p class="section-title">Audit History</p>
            <div class="audit-list">
              <div v-for="log in detailModal.auditLog" :key="log.id" class="audit-row">
                <span class="audit-action">{{ log.action }}</span>
                <span class="audit-who">{{ log.performer ? `${log.performer.first_name} ${log.performer.last_name}` : 'System' }}</span>
                <span class="audit-time">{{ fmtDate(log.created_at) }}</span>
                <span v-if="log.meta?.version" class="audit-meta">v{{ log.meta.version }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="detailModal.open = false">Close</button>
        </div>
      </div>
    </div>

    <!-- ══  Category Management Modal  ══ -->
    <div class="modal-overlay" v-if="catModal.open" @click.self="catModal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Document Categories</p>
          <button class="modal-close" @click="catModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Name <span class="req">*</span></label>
            <input class="form-input" v-model="catModal.name" placeholder="Category name" />
          </div>
          <div class="form-row">
            <label class="form-label">Description</label>
            <input class="form-input" v-model="catModal.description" placeholder="Optional description" />
          </div>
          <div style="margin-top:8px">
            <button class="btn-primary-action" style="width:100%" :disabled="saving" @click="saveCategory">
              {{ saving ? 'Saving…' : 'Add Category' }}
            </button>
          </div>
          <div v-if="categories.length" class="cat-list">
            <div v-for="c in categories" :key="c.id" class="cat-row">
              <span class="cat-name">{{ c.name }}</span>
              <button v-if="permission.delete_documents" class="icon-btn icon-btn--red" @click="deleteCategory(c)"><font-awesome-icon icon="trash" /></button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="catModal.open = false">Close</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'HrDocumentsPage',

  data() {
    return {
      loading:    false,
      saving:     false,
      documents:  [],
      categories: [],
      departments: [],
      employees:  [],
      meta: { current_page: 1, last_page: 1 },
      page: 1,
      filters: { search: '', status: '', category_id: '', audience: '' },
      searchTimer: null,
      deptOpen: false, deptSearch: '',
      empOpen:  false, empSearch:  '',

      docModal: {
        open: false, isEdit: false, id: null,
        title: '', description: '', category_id: '', version: '1.0',
        audience: 'all', audience_ids: [],
        requires_acknowledgement: false,
        expires_at: '', review_date: '',
        file: null, currentFileName: '',
      },

      detailModal: { open: false, doc: null, auditLog: [] },
      catModal:    { open: false, name: '', description: '' },
    };
  },

  computed: {
    filteredDepts() {
      const q = this.deptSearch.toLowerCase();
      return q ? this.departments.filter(d => d.name.toLowerCase().includes(q)) : this.departments;
    },
    filteredEmps() {
      const q = this.empSearch.toLowerCase();
      return q ? this.employees.filter(e => `${e.first_name} ${e.last_name}`.toLowerCase().includes(q)) : this.employees;
    },
    permission() {
      const p = this.$store?.state?.getCurrentUserPermission || {};
      return {
        view_documents:    p.view_documents    || p.hr_module,
        upload_documents:  p.upload_documents  || p.hr_module,
        edit_documents:    p.edit_documents    || p.hr_module,
        publish_documents: p.publish_documents || p.hr_module,
        archive_documents: p.archive_documents || p.hr_module,
        delete_documents:  p.delete_documents  || p.hr_module,
      };
    },
  },

  mounted() {
    if (this.permission.view_documents) {
      this.fetchDocuments();
      this.fetchCategories();
      this.fetchDepartments();
      this.fetchEmployees();
    }
  },

  methods: {
    // ── Data fetching ──────────────────────────────────────────────────────
    async fetchDocuments() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/documents', {
          params: { ...this.filters, page: this.page, length: 15 },
        });
        this.documents = data.data?.data || [];
        this.meta = { current_page: data.data?.current_page || 1, last_page: data.data?.last_page || 1 };
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load documents');
      } finally {
        this.loading = false;
      }
    },

    async fetchCategories() {
      try {
        const { data } = await axios.get('/hr/documents/categories', { params: { all: true } });
        this.categories = data.data || [];
      } catch { /* silent */ }
    },

    async fetchDepartments() {
      try {
        const { data } = await axios.get('/payroll/department/active');
        this.departments = data.data || [];
      } catch { /* silent */ }
    },

    async fetchEmployees() {
      try {
        const { data } = await axios.get('/payroll/employees/active', { params: { length: 500 } });
        this.employees = data.data || [];
      } catch { /* silent */ }
    },

    goPage(p) { this.page = p; this.fetchDocuments(); },

    debounceSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.fetchDocuments(); }, 400);
    },

    // ── Document CRUD ──────────────────────────────────────────────────────
    openCreateDoc() {
      this.deptOpen = false; this.deptSearch = '';
      this.empOpen  = false; this.empSearch  = '';
      this.docModal = {
        open: true, isEdit: false, id: null,
        title: '', description: '', category_id: '', version: '1.0',
        audience: 'all', audience_ids: [],
        requires_acknowledgement: false,
        expires_at: '', review_date: '',
        file: null, currentFileName: '',
      };
    },

    openEdit(doc) {
      this.deptOpen = false; this.deptSearch = '';
      this.empOpen  = false; this.empSearch  = '';
      this.docModal = {
        open: true, isEdit: true, id: doc.id,
        title: doc.title, description: doc.description || '',
        category_id: doc.category_id || '', version: doc.version || '1.0',
        audience: doc.audience || 'all', audience_ids: doc.audience_ids || [],
        requires_acknowledgement: !!doc.requires_acknowledgement,
        expires_at: doc.expires_at || '', review_date: doc.review_date || '',
        file: null, currentFileName: doc.file_name || '',
      };
    },

    onFileSelected(e) { this.docModal.file = e.target.files[0] || null; },

    onAudienceChange() {
      this.docModal.audience_ids = [];
      this.deptOpen = false; this.deptSearch = '';
      this.empOpen  = false; this.empSearch  = '';
    },

    deptName(id) {
      const d = this.departments.find(x => x.id === id);
      return d ? d.name : id;
    },
    empName(id) {
      const e = this.employees.find(x => x.id === id);
      return e ? `${e.first_name} ${e.last_name}` : id;
    },
    removeAudienceId(id) {
      this.docModal.audience_ids = this.docModal.audience_ids.filter(x => x !== id);
    },

    async saveDocument() {
      if (!this.docModal.title.trim()) {
        this.$toast?.error('Title is required');
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append('title',        this.docModal.title);
        fd.append('description',  this.docModal.description || '');
        fd.append('version',      this.docModal.version || '1.0');
        fd.append('audience',     this.docModal.audience);
        fd.append('requires_acknowledgement', this.docModal.requires_acknowledgement ? '1' : '0');
        if (this.docModal.category_id) fd.append('category_id', this.docModal.category_id);
        if (this.docModal.expires_at)  fd.append('expires_at',  this.docModal.expires_at);
        if (this.docModal.review_date) fd.append('review_date', this.docModal.review_date);
        if (this.docModal.audience_ids?.length) {
          this.docModal.audience_ids.forEach(id => fd.append('audience_ids[]', id));
        }
        if (this.docModal.file) fd.append('file', this.docModal.file);

        if (this.docModal.isEdit) {
          await axios.post(`/hr/documents/${this.docModal.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          this.$toast?.success('Document updated');
        } else {
          await axios.post('/hr/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          this.$toast?.success('Document uploaded');
        }

        this.docModal.open = false;
        this.fetchDocuments();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to save document');
      } finally {
        this.saving = false;
      }
    },

    confirmPublish(doc) {
      if (!confirm(`Publish "${doc.title}"? It will become visible to employees and notifications will be sent.`)) return;
      this.publishDoc(doc);
    },

    async publishDoc(doc) {
      try {
        await axios.put(`/hr/documents/${doc.id}/publish`);
        this.$toast?.success('Document published');
        this.fetchDocuments();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to publish');
      }
    },

    confirmArchive(doc) {
      if (!confirm(`Archive "${doc.title}"? It will be hidden from employees.`)) return;
      this.archiveDoc(doc);
    },

    async archiveDoc(doc) {
      try {
        await axios.put(`/hr/documents/${doc.id}/archive`);
        this.$toast?.success('Document archived');
        this.fetchDocuments();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to archive');
      }
    },

    confirmDelete(doc) {
      if (!confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
      this.deleteDoc(doc);
    },

    async deleteDoc(doc) {
      try {
        await axios.delete(`/hr/documents/${doc.id}`);
        this.$toast?.success('Document deleted');
        this.fetchDocuments();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to delete');
      }
    },

    async downloadDoc(doc) {
      try {
        const res  = await axios.get(`/hr/documents/${doc.id}/download`, { responseType: 'arraybuffer' });
        // Force application/octet-stream so the browser always downloads (never previews)
        const blob = new Blob([res.data], { type: 'application/octet-stream' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = doc.file_name || 'document';
        a.style.display = 'none';
        document.body.appendChild(a);
        // bubbles:false prevents Vue Router's document-level click handler from
        // intercepting the blob URL and opening it in a new tab
        a.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true }));
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
      } catch (e) {
        this.$toast?.error('Download failed');
      }
    },

    // ── Detail / Audit log ─────────────────────────────────────────────────
    async openDetail(doc) {
      try {
        const [detailRes, logRes] = await Promise.all([
          axios.get(`/hr/documents/${doc.id}`),
          axios.get(`/hr/documents/${doc.id}/audit-log`),
        ]);
        this.detailModal = {
          open:     true,
          doc:      detailRes.data.data,
          auditLog: logRes.data.data || [],
        };
      } catch (e) {
        this.$toast?.error('Failed to load document detail');
      }
    },

    openAuditLog(doc) { this.openDetail(doc); },

    // ── Categories ─────────────────────────────────────────────────────────
    openCategoryModal() {
      this.catModal = { open: true, name: '', description: '' };
    },

    async saveCategory() {
      if (!this.catModal.name.trim()) { this.$toast?.error('Name is required'); return; }
      this.saving = true;
      try {
        await axios.post('/hr/documents/categories', {
          name: this.catModal.name, description: this.catModal.description,
        });
        this.$toast?.success('Category added');
        this.catModal.name = '';
        this.catModal.description = '';
        this.fetchCategories();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to add category');
      } finally {
        this.saving = false;
      }
    },

    async deleteCategory(cat) {
      if (!confirm(`Delete category "${cat.name}"?`)) return;
      try {
        await axios.delete(`/hr/documents/categories/${cat.id}`);
        this.$toast?.success('Category deleted');
        this.fetchCategories();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to delete category');
      }
    },

    // ── Helpers ────────────────────────────────────────────────────────────
    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },
    statusClass(s) {
      return { draft: 'chip--amber', published: 'chip--green', archived: 'chip--gray' }[s] || 'chip--gray';
    },
    audienceLabel(doc) {
      if (doc.audience === 'all') return 'All Employees';
      if (doc.audience === 'department') return `${(doc.audience_ids || []).length} Dept(s)`;
      if (doc.audience === 'employee') return `${(doc.audience_ids || []).length} Employee(s)`;
      return doc.audience;
    },
    fileIcon(mime) {
      if (!mime) return 'file-alt';
      if (mime.includes('pdf')) return 'file-pdf';
      if (mime.includes('word') || mime.includes('document')) return 'file-word';
      if (mime.includes('excel') || mime.includes('sheet')) return 'file-excel';
      if (mime.includes('presentation') || mime.includes('powerpoint')) return 'file-powerpoint';
      if (mime.includes('image')) return 'file-image';
      return 'file-alt';
    },
    isExpired(d) { return d && new Date(d) < new Date(); },
  },
};
</script>

<style scoped>
.page-header    { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title     { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub       { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }

.alert-box { display:flex; align-items:center; gap:10px; padding:14px 16px; border-radius:10px; font-size:13px; font-weight:500; }
.alert-box--warn { background:#fef9c3; border:1px solid #fde047; color:#854d0e; }

.filter-bar { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.filter-input  { flex:1; min-width:180px; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:9px 10px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; min-width:140px; }

.table-card   { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; overflow-x:auto; }
.data-table   { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:11px 12px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:11px 12px; color:#1a2b45; vertical-align:middle; }
.fw-600 { font-weight:600; }
.doc-title-cell { cursor:pointer; }
.doc-title-cell:hover { color:#004AAD; }
.file-icon { color:#7a90b0; margin-right:6px; }
.ack-badge { background:#dbeafe; color:#1d4ed8; font-size:9px; font-weight:700; padding:2px 5px; border-radius:4px; margin-left:6px; }
.count-cell { text-align:center; font-weight:600; color:#004AAD; }
.text-danger { color:#dc2626; }
.ver-chip { background:#f1f5f9; color:#475569; font-size:11px; font-weight:600; padding:2px 8px; border-radius:20px; }

.chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; text-transform:capitalize; }
.chip--green { background:#dcfce7; color:#16a34a; }
.chip--amber { background:#fef9c3; color:#ca8a04; }
.chip--gray  { background:#f1f5f9; color:#64748b; }

.action-cell { display:flex; align-items:center; gap:4px; white-space:nowrap; }
.icon-btn    { width:28px; height:28px; border-radius:6px; border:none; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; font-size:12px; }
.icon-btn--green  { background:#dcfce7; color:#16a34a; }
.icon-btn--blue   { background:#dbeafe; color:#1d4ed8; }
.icon-btn--teal   { background:#ccfbf1; color:#0d9488; }
.icon-btn--gray   { background:#f1f5f9; color:#64748b; }
.icon-btn--red    { background:#fee2e2; color:#dc2626; }
.icon-btn--purple { background:#ede9fe; color:#7c3aed; }

.pagination { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #f0f4f8; }
.page-btn   { background:#fff; border:1px solid #dde5f0; border-radius:6px; padding:4px 12px; font-size:15px; cursor:pointer; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info  { font-size:13px; color:#7a90b0; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row-head,.sk-row { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-row-head { height:40px; background:#f8fafc; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-box         { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:14px; }
.empty-illustration { width:96px; height:96px; border-radius:50%; background:#f0f7ff; display:flex; align-items:center; justify-content:center; }
.empty-icon        { font-size:40px; color:#004AAD; opacity:.5; }
.empty-title       { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.empty-sub         { font-size:13px; color:#7a90b0; margin:0; text-align:center; }

/* Modal */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--lg { max-width:720px; }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.form-grid    { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.form-row     { display:flex; flex-direction:column; gap:5px; }
.form-row--full { grid-column:1 / -1; }
.form-label   { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input   { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }
.file-input { font-size:13px; }
.file-hint  { font-size:11px; color:#7a90b0; margin:3px 0 0; }
.checkbox-wrap { display:flex; align-items:center; gap:8px; font-size:13px; color:#1a2b45; cursor:pointer; }

.detail-grid  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.detail-item  { display:flex; flex-direction:column; gap:3px; }
.detail-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.detail-value { font-size:14px; color:#1a2b45; }

.notes-box    { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:12px 14px; }
.notes-label  { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin:0 0 6px; }
.notes-content { font-size:13px; color:#1a2b45; margin:0; line-height:1.6; }

.section-title { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#4a5e78; margin:4px 0 8px; }
.audit-list   { display:flex; flex-direction:column; gap:6px; }
.audit-row    { display:flex; align-items:center; gap:10px; padding:8px 10px; background:#f8fafc; border-radius:6px; font-size:12px; }
.audit-action { font-weight:700; color:#004AAD; text-transform:capitalize; min-width:90px; }
.audit-who    { flex:1; color:#475569; }
.audit-time   { color:#94a3b8; white-space:nowrap; }
.audit-meta   { background:#dbeafe; color:#1d4ed8; font-size:10px; font-weight:700; padding:2px 6px; border-radius:4px; }

.cat-list { display:flex; flex-direction:column; gap:6px; margin-top:12px; }
.cat-row  { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; background:#f8fafc; border-radius:8px; }
.cat-name { font-size:13px; font-weight:600; color:#1a2b45; }

/* ── Multiselect ─────────────────────────────────────────────────────── */
.ms-wrap     { position:relative; }
.ms-backdrop { position:fixed; inset:0; z-index:99; }
.ms-trigger  {
  display:flex; align-items:flex-start; justify-content:space-between; gap:8px;
  min-height:38px; padding:6px 10px; border:1px solid #dde5f0; border-radius:8px;
  background:#fff; cursor:pointer; transition:border-color .15s;
}
.ms-trigger:hover { border-color:#004AAD; }
.ms-tags     { display:flex; flex-wrap:wrap; gap:5px; flex:1; }
.ms-tag      { display:inline-flex; align-items:center; gap:4px; background:#e8f0fe; color:#1a40a6; font-size:12px; font-weight:600; padding:3px 8px; border-radius:20px; }
.ms-tag-rm   { background:none; border:none; color:#1a40a6; cursor:pointer; font-size:14px; line-height:1; padding:0; opacity:.7; }
.ms-tag-rm:hover { opacity:1; }
.ms-placeholder { font-size:13px; color:#aab8cc; align-self:center; }
.ms-arrow    { font-size:15px; color:#7a90b0; align-self:center; flex-shrink:0; transition:transform .2s; }
.ms-arrow--open { transform:rotate(180deg); }
.ms-dropdown {
  position:absolute; top:calc(100% + 4px); left:0; right:0; z-index:100;
  background:#fff; border:1px solid #dde5f0; border-radius:10px;
  box-shadow:0 8px 24px rgba(0,0,0,.12); overflow:hidden;
}
.ms-search {
  display:block; width:100%; padding:9px 12px; border:none; border-bottom:1px solid #f0f4f8;
  font-size:13px; color:#1a2b45; outline:none; background:#f8fafc;
}
.ms-list  { max-height:220px; overflow-y:auto; padding:4px 0; }
.ms-option {
  display:flex; align-items:center; gap:9px; padding:8px 12px;
  font-size:13px; color:#1a2b45; cursor:pointer; transition:background .1s;
}
.ms-option:hover   { background:#f4f7fb; }
.ms-option--on     { background:#eef3ff; }
.ms-option--on span { font-weight:600; color:#004AAD; }
.ms-check  { accent-color:#004AAD; width:15px; height:15px; flex-shrink:0; cursor:pointer; }
.ms-empty  { padding:12px; text-align:center; font-size:13px; color:#aab8cc; }
</style>
